import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import {
  ActionIcon,
  Box,
  Menu,
  ScrollAreaAutosize,
  Text,
  Tooltip,
  useMantineColorScheme,
} from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { getRenderDraggable } from "./utils";

import { IconFilterTable } from "@/components/icons";

import type { DropResult } from "@hello-pangea/dnd";
import type { ActionIconProps } from "@mantine/core";
import type { FC } from "react";
import type { ListTableColumnFiltered } from "../../types";

export type ListTableColumnsOrganizerProps = {
  columns: ListTableColumnFiltered[];
  disabled?: boolean;
  iconSize?: number;
  label?: string;
  onChange: (columns: ListTableColumnFiltered[]) => void;
  tooltip?: string;
  labelTranslation?: string;
} & Pick<ActionIconProps, "color" | "loading" | "radius" | "size" | "variant">;

const MAX_DROPDOWN_HEIGHT = 400;
const MIN_DROPDOWN_HEIGHT = 150;
const VIEWPORT_PADDING = 20;

export const ListTableColumnsOrganizer: FC<ListTableColumnsOrganizerProps> = ({
  columns,
  disabled = false,
  iconSize = 24,
  label,
  onChange,
  tooltip,
  labelTranslation,
  ...props
}) => {
  const { t } = useTranslation();
  const renderDraggable = getRenderDraggable(columns, getToggleColumn, labelTranslation);
  const { colorScheme } = useMantineColorScheme();

  const isDark = colorScheme === "dark";

  const buttonRef = useRef<HTMLButtonElement>(null);
  const [maxHeight, setMaxHeight] = useState(MIN_DROPDOWN_HEIGHT);

  const calculateMaxHeight = () => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    const spaceBelow = viewportHeight - rect.bottom - VIEWPORT_PADDING;
    const spaceAbove = rect.top - VIEWPORT_PADDING;

    const availableHeight = spaceBelow >= MIN_DROPDOWN_HEIGHT ? spaceBelow : spaceAbove;

    setMaxHeight(Math.min(MAX_DROPDOWN_HEIGHT, Math.max(MIN_DROPDOWN_HEIGHT, availableHeight)));
  };

  useEffect(() => {
    calculateMaxHeight();
    window.addEventListener("resize", calculateMaxHeight);
    return () => {
      window.removeEventListener("resize", calculateMaxHeight);
    };
  }, []);

  function getToggleColumn(name: string) {
    return () => {
      onChange(
        columns.map((item) => {
          if (item.id === name) {
            return {
              ...item,
              active: !item.active,
            };
          }
          return item;
        })
      );
    };
  }

  function handleDragEnd({ destination, source }: DropResult) {
    if (!destination) return;
    const from = source.index;
    const to = destination.index;
    if (from === to) return;

    const draggableColumns = columns.filter((c) => !c.disabled);
    const fromId = draggableColumns[from]?.id;
    const toId = draggableColumns[to]?.id;
    if (!fromId || !toId) return;

    const result = [...columns];
    const fromIndex = result.findIndex((col) => col.id === fromId);
    const toIndex = result.findIndex((col) => col.id === toId);
    if (fromIndex === -1 || toIndex === -1) return;

    const [removed] = result.splice(fromIndex, 1);
    result.splice(toIndex, 0, removed);

    onChange(result);
  }

  return (
    <Menu
      closeOnItemClick={false}
      disabled={disabled}
      onOpen={calculateMaxHeight}
      position="bottom-end"
      shadow="md"
    >
      <Menu.Target>
        <Tooltip
          label={tooltip}
          disabled={!tooltip || disabled}
          events={{ hover: true, focus: true, touch: true }}
        >
          <ActionIcon
            ref={buttonRef}
            variant="light"
            size="input-sm"
            {...props}
            disabled={disabled}
            style={{
              backgroundColor: isDark ? "var(--mantine-color-primaryDark-7)" : undefined,
            }}
            aria-label={t("common.organizeColumns")}
          >
            <IconFilterTable size={iconSize} />
          </ActionIcon>
        </Tooltip>
      </Menu.Target>
      <Menu.Dropdown
        py="md"
        style={{
          zIndex: 10,
        }}
      >
        {/*  {label && (
          <Menu.Label fz="md" c="dark">
            {label}
          </Menu.Label>
        )} */}
        {label && (
          <Text fz="sm" pl="sm">
            {label}
          </Text>
        )}
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable
            droppableId="columns-organizer"
            direction="vertical"
            renderClone={renderDraggable}
          >
            {({ droppableProps, innerRef, placeholder }) => (
              <ScrollAreaAutosize mah={maxHeight} type="auto" scrollbarSize={4} offsetScrollbars>
                <Box {...droppableProps} ref={innerRef}>
                  {columns
                    .filter((c) => !c.disabled)
                    .map(({ id }, index) => (
                      <Draggable key={id} index={index} draggableId={id}>
                        {renderDraggable}
                      </Draggable>
                    ))}
                  {placeholder}
                </Box>
              </ScrollAreaAutosize>
            )}
          </Droppable>
        </DragDropContext>
      </Menu.Dropdown>
    </Menu>
  );
};
